import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { AccordionGroup } from '@sola/ark-core/molecules/accordion-group/accordion-group.tsx';
import type { ChildDetails } from '@sola/schema/dotcom/children/child.ts';
import type { HouseholdMemberItem } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
import type { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { getAgeFromBirthday } from '@sola/shared-utils/children/utils.ts';
interface ChildDetailsOverviewListItemProps {
  title?: string;
  children: React.ReactNode;
}
const ChildDetailsOverviewListItem: React.FC<ChildDetailsOverviewListItemProps> = t0 => {
  const $ = _c(7);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = title && <span className="label-potent">{title}{": "}</span>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <span>{children}</span>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <li className="body-sm label-neutral-mild">{t1}{t2}</li>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
interface ChildDetailsListProps {
  children: React.ReactNode;
}
const ChildDetailsList: React.FC<ChildDetailsListProps> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <ul className="p-0 list-none m-0 flex flex-col gap-snug-sm">{children}</ul>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
interface ChildDetailsListItemProps {
  title: string;
  children: React.ReactNode;
}
const ChildDetailsListItem: React.FC<ChildDetailsListItemProps> = t0 => {
  const $ = _c(7);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = title && <span className="label-potent">{title}{": "}</span>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <ChildDetailsList>{children}</ChildDetailsList>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <li className="flex flex-col body-sm label-neutral-mild">{t1}{t2}</li>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
interface ChildDetailsAccordionSectionProps {
  children: React.ReactNode;
}
const ChildDetailsAccordionSection: React.FC<ChildDetailsAccordionSectionProps> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="p-0 list-none m-0 flex flex-col gap-comfy-sm">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const getPrimaryCareGiver = (householdMemberList: HouseholdMemberItem[]) => {
  return householdMemberList.find(member => member.isPrimaryCaregiver)?.householdMemberRole;
};
export const ChildBioAccordion = t0 => {
  const $ = _c(144);
  const {
    child,
    commitment
  } = t0;
  const {
    t
  } = useTranslation("my");
  const preferredName = child.name.preferred;
  let t1;
  if ($[0] !== t) {
    t1 = t("sponsorshipRelationship.childBio.overview.title");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("sponsorshipRelationship.childBio.overview.age");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== child.birthDate) {
    t3 = getAgeFromBirthday(child.birthDate);
    $[4] = child.birthDate;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <ChildDetailsOverviewListItem title={t2}>{t3}</ChildDetailsOverviewListItem>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("sponsorshipRelationship.childBio.overview.birthday");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== child.birthDate || $[12] !== t) {
    t6 = t("sponsorshipRelationship.childBio.overview.birthdayValue", {
      date: child.birthDate
    });
    $[11] = child.birthDate;
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <ChildDetailsOverviewListItem title={t5}>{t6}</ChildDetailsOverviewListItem>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t) {
    t8 = t("sponsorshipRelationship.childBio.overview.gender");
    $[17] = t;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== child.gender || $[20] !== t8) {
    t9 = <ChildDetailsOverviewListItem title={t8}>{child.gender}</ChildDetailsOverviewListItem>;
    $[19] = child.gender;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t) {
    t10 = t("sponsorshipRelationship.childBio.overview.location");
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== child.country || $[25] !== t10) {
    t11 = <ChildDetailsOverviewListItem title={t10}>{child.country}</ChildDetailsOverviewListItem>;
    $[24] = child.country;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t) {
    t12 = t("sponsorshipRelationship.childBio.overview.childId");
    $[27] = t;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== child.localId || $[30] !== t12) {
    t13 = <ChildDetailsOverviewListItem title={t12}>{child.localId}</ChildDetailsOverviewListItem>;
    $[29] = child.localId;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t11 || $[33] !== t13 || $[34] !== t4 || $[35] !== t7 || $[36] !== t9) {
    t14 = <ChildDetailsList>{t4}{t7}{t9}{t11}{t13}</ChildDetailsList>;
    $[32] = t11;
    $[33] = t13;
    $[34] = t4;
    $[35] = t7;
    $[36] = t9;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== commitment || $[39] !== preferredName || $[40] !== t) {
    t15 = commitment?.dates?.start && <p className="body-sm label-neutral-mild">{t("sponsorshipRelationship.childBio.overview.sponsorshipLength", {
        childName: preferredName,
        date: commitment.dates.start
      })}</p>;
    $[38] = commitment;
    $[39] = preferredName;
    $[40] = t;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== t14 || $[43] !== t15) {
    t16 = <ChildDetailsAccordionSection>{t14}{t15}</ChildDetailsAccordionSection>;
    $[42] = t14;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t1 || $[46] !== t16) {
    t17 = {
      header: t1,
      children: t16
    };
    $[45] = t1;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== t) {
    t18 = t("sponsorshipRelationship.childBio.education.title");
    $[48] = t;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== child.education.academicPerformance || $[51] !== t) {
    t19 = child.education.academicPerformance && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.education.performance")}>{child.education.academicPerformance}</ChildDetailsListItem>;
    $[50] = child.education.academicPerformance;
    $[51] = t;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== child.education.gradeLevel.us || $[54] !== t) {
    t20 = child.education.gradeLevel.us && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.education.gradeLevel")}>{child.education.gradeLevel.us}</ChildDetailsListItem>;
    $[53] = child.education.gradeLevel.us;
    $[54] = t;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== child.education.gradeLevel.local || $[57] !== t) {
    t21 = child.education.gradeLevel.local && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.education.localGradeName")}>{child.education.gradeLevel.local}</ChildDetailsListItem>;
    $[56] = child.education.gradeLevel.local;
    $[57] = t;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== child.interests.favoriteSchoolSubject || $[60] !== t) {
    t22 = child.interests.favoriteSchoolSubject.length > 0 && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.education.favoriteSubjects")}><ChildDetailsList>{child.interests.favoriteSchoolSubject.map(_temp)}</ChildDetailsList></ChildDetailsListItem>;
    $[59] = child.interests.favoriteSchoolSubject;
    $[60] = t;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  let t23;
  if ($[62] !== child.plannedCompletion.date || $[63] !== t) {
    t23 = child.plannedCompletion.date && <ChildDetailsListItem title={t("my:sponsorshipRelationship.childBio.education.estimatedCompletionDate")}>{t("my:sponsorshipRelationship.childBio.education.estimatedCompletionDateValue", {
        date: child.plannedCompletion.date
      })}</ChildDetailsListItem>;
    $[62] = child.plannedCompletion.date;
    $[63] = t;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  let t24;
  if ($[65] !== t) {
    t24 = t("sponsorshipRelationship.childBio.education.howYourSponsorshipHelps");
    $[65] = t;
    $[66] = t24;
  } else {
    t24 = $[66];
  }
  let t25;
  if ($[67] !== child.gender || $[68] !== preferredName || $[69] !== t) {
    t25 = t("sponsorshipRelationship.childBio.education.howYourSponsorshipHelpsSummary", {
      childName: preferredName,
      context: child.gender
    });
    $[67] = child.gender;
    $[68] = preferredName;
    $[69] = t;
    $[70] = t25;
  } else {
    t25 = $[70];
  }
  let t26;
  if ($[71] !== t24 || $[72] !== t25) {
    t26 = <ChildDetailsListItem title={t24}>{t25}</ChildDetailsListItem>;
    $[71] = t24;
    $[72] = t25;
    $[73] = t26;
  } else {
    t26 = $[73];
  }
  let t27;
  if ($[74] !== child.plannedCompletion.date || $[75] !== t) {
    t27 = child.plannedCompletion.date && <p className="body-xs label-neutral-mild"><IntlBlock i18nKey="my:sponsorshipRelationship.childBio.education.estimatedCompletionDateFootnote" components={{
        completionDateLink: <Link className="body-xs label-neutral-mild underline" href={t("my:sponsorshipRelationship.childBio.education.estimatedCompletionDateLink")} />
      }} /></p>;
    $[74] = child.plannedCompletion.date;
    $[75] = t;
    $[76] = t27;
  } else {
    t27 = $[76];
  }
  let t28;
  if ($[77] !== t19 || $[78] !== t20 || $[79] !== t21 || $[80] !== t22 || $[81] !== t23 || $[82] !== t26 || $[83] !== t27) {
    t28 = <ChildDetailsAccordionSection>{t19}{t20}{t21}{t22}{t23}{t26}{t27}</ChildDetailsAccordionSection>;
    $[77] = t19;
    $[78] = t20;
    $[79] = t21;
    $[80] = t22;
    $[81] = t23;
    $[82] = t26;
    $[83] = t27;
    $[84] = t28;
  } else {
    t28 = $[84];
  }
  let t29;
  if ($[85] !== t18 || $[86] !== t28) {
    t29 = {
      header: t18,
      children: t28
    };
    $[85] = t18;
    $[86] = t28;
    $[87] = t29;
  } else {
    t29 = $[87];
  }
  let t30;
  if ($[88] !== t) {
    t30 = t("sponsorshipRelationship.childBio.family.title");
    $[88] = t;
    $[89] = t30;
  } else {
    t30 = $[89];
  }
  let t31;
  if ($[90] !== child.householdList || $[91] !== t) {
    t31 = child.householdList?.[0]?.householdMemberList && child.householdList[0].householdMemberList.length > 0 && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.family.primaryCaregiver")}>{getPrimaryCareGiver(child.householdList[0].householdMemberList)}</ChildDetailsListItem>;
    $[90] = child.householdList;
    $[91] = t;
    $[92] = t31;
  } else {
    t31 = $[92];
  }
  let t32;
  if ($[93] !== child.householdDuty || $[94] !== t) {
    t32 = child.householdDuty.length > 0 && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.family.duties")}><ChildDetailsList>{child.householdDuty.map(_temp2)}</ChildDetailsList></ChildDetailsListItem>;
    $[93] = child.householdDuty;
    $[94] = t;
    $[95] = t32;
  } else {
    t32 = $[95];
  }
  let t33;
  if ($[96] !== t) {
    t33 = t("sponsorshipRelationship.childBio.family.howYourSponsorshipHelps");
    $[96] = t;
    $[97] = t33;
  } else {
    t33 = $[97];
  }
  let t34;
  if ($[98] !== t) {
    t34 = t("sponsorshipRelationship.childBio.family.howYourSponsorshipHelpsParagraph");
    $[98] = t;
    $[99] = t34;
  } else {
    t34 = $[99];
  }
  let t35;
  if ($[100] !== t33 || $[101] !== t34) {
    t35 = <ChildDetailsListItem title={t33}>{t34}</ChildDetailsListItem>;
    $[100] = t33;
    $[101] = t34;
    $[102] = t35;
  } else {
    t35 = $[102];
  }
  let t36;
  if ($[103] !== t31 || $[104] !== t32 || $[105] !== t35) {
    t36 = <ChildDetailsAccordionSection>{t31}{t32}{t35}</ChildDetailsAccordionSection>;
    $[103] = t31;
    $[104] = t32;
    $[105] = t35;
    $[106] = t36;
  } else {
    t36 = $[106];
  }
  let t37;
  if ($[107] !== t30 || $[108] !== t36) {
    t37 = {
      header: t30,
      children: t36
    };
    $[107] = t30;
    $[108] = t36;
    $[109] = t37;
  } else {
    t37 = $[109];
  }
  let t38;
  if ($[110] !== t) {
    t38 = t("sponsorshipRelationship.childBio.activities.title");
    $[110] = t;
    $[111] = t38;
  } else {
    t38 = $[111];
  }
  let t39;
  if ($[112] !== child.interests.christianActivityName || $[113] !== t) {
    t39 = child.interests.christianActivityName.length > 0 && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.activities.christian")}><ChildDetailsList>{child.interests.christianActivityName.map(_temp3)}</ChildDetailsList></ChildDetailsListItem>;
    $[112] = child.interests.christianActivityName;
    $[113] = t;
    $[114] = t39;
  } else {
    t39 = $[114];
  }
  let t40;
  if ($[115] !== child.interests.favoriteProjectActivity || $[116] !== t) {
    t40 = child.interests.favoriteProjectActivity.length > 0 && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.activities.project")}><ChildDetailsList>{child.interests.favoriteProjectActivity.map(_temp4)}</ChildDetailsList></ChildDetailsListItem>;
    $[115] = child.interests.favoriteProjectActivity;
    $[116] = t;
    $[117] = t40;
  } else {
    t40 = $[117];
  }
  let t41;
  if ($[118] !== child.interests.thingsILike || $[119] !== preferredName || $[120] !== t) {
    t41 = child.interests.thingsILike.length > 0 && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.activities.thingsChildLikes", {
      childName: preferredName
    })}><ChildDetailsList>{child.interests.thingsILike.map(_temp5)}</ChildDetailsList></ChildDetailsListItem>;
    $[118] = child.interests.thingsILike;
    $[119] = preferredName;
    $[120] = t;
    $[121] = t41;
  } else {
    t41 = $[121];
  }
  let t42;
  if ($[122] !== t) {
    t42 = t("sponsorshipRelationship.childBio.activities.howYourSponsorshipHelps");
    $[122] = t;
    $[123] = t42;
  } else {
    t42 = $[123];
  }
  let t43;
  if ($[124] !== child.gender || $[125] !== preferredName || $[126] !== t) {
    t43 = t("my:sponsorshipRelationship.childBio.activities.howYourSponsorshipHelpsParagraph", {
      childName: preferredName,
      context: child.gender
    });
    $[124] = child.gender;
    $[125] = preferredName;
    $[126] = t;
    $[127] = t43;
  } else {
    t43 = $[127];
  }
  let t44;
  if ($[128] !== t42 || $[129] !== t43) {
    t44 = <ChildDetailsListItem title={t42}>{t43}</ChildDetailsListItem>;
    $[128] = t42;
    $[129] = t43;
    $[130] = t44;
  } else {
    t44 = $[130];
  }
  let t45;
  if ($[131] !== t39 || $[132] !== t40 || $[133] !== t41 || $[134] !== t44) {
    t45 = <ChildDetailsAccordionSection>{t39}{t40}{t41}{t44}</ChildDetailsAccordionSection>;
    $[131] = t39;
    $[132] = t40;
    $[133] = t41;
    $[134] = t44;
    $[135] = t45;
  } else {
    t45 = $[135];
  }
  let t46;
  if ($[136] !== t38 || $[137] !== t45) {
    t46 = {
      header: t38,
      children: t45
    };
    $[136] = t38;
    $[137] = t45;
    $[138] = t46;
  } else {
    t46 = $[138];
  }
  let t47;
  if ($[139] !== t17 || $[140] !== t29 || $[141] !== t37 || $[142] !== t46) {
    const accordionItems = [t17, t29, t37, t46];
    t47 = <AccordionGroup items={accordionItems} />;
    $[139] = t17;
    $[140] = t29;
    $[141] = t37;
    $[142] = t46;
    $[143] = t47;
  } else {
    t47 = $[143];
  }
  return t47;
};
function _temp(subject) {
  return <ChildDetailsOverviewListItem key={`favorite-school-subject-${subject}`}>{subject}</ChildDetailsOverviewListItem>;
}
function _temp2(duty) {
  return <ChildDetailsOverviewListItem key={`household-duty-${duty}`}>{duty}</ChildDetailsOverviewListItem>;
}
function _temp3(activity) {
  return <ChildDetailsOverviewListItem key={`christian-activity-${activity}`}>{activity}</ChildDetailsOverviewListItem>;
}
function _temp4(activity_0) {
  return <ChildDetailsOverviewListItem key={`favorite-project-activity-${activity_0}`}>{activity_0}</ChildDetailsOverviewListItem>;
}
function _temp5(activity_1) {
  return <ChildDetailsOverviewListItem key={`things-i-like-${activity_1}`}>{activity_1}</ChildDetailsOverviewListItem>;
}